import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    InputGroup, Form, OverlayTrigger, Tooltip, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";

export default function HistoriqueInventaires() {
    const navigate = useNavigate();
    const [inventaires, setInventaires] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchInventaires();
    }, []);

    const fetchInventaires = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/inventaires.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setInventaires(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { color: '#f59e0b', icon: 'ri-time-line', label: 'Brouillon' },
            valide: { color: '#10b981', icon: 'ri-check-double-line', label: 'Validé' },
            annule: { color: '#ef4444', icon: 'ri-close-circle-line', label: 'Annulé' }
        };
        const config = configs[statut] || configs.brouillon;
        
        return (
            <span className="status-badge" style={{ background: `${config.color}15`, color: config.color }}>
                <i className={`${config.icon} me-1 fs-10`}></i>
                {config.label}
            </span>
        );
    };

    const filteredInventaires = inventaires.filter(i => {
        const matchesSearch = i.reference_inventaire?.toLowerCase().includes(filterText.toLowerCase());
        
        if (dateDebut && new Date(i.date_inventaire) < new Date(dateDebut)) return false;
        if (dateFin && new Date(i.date_inventaire) > new Date(dateFin)) return false;
        
        return matchesSearch;
    });

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredInventaires.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredInventaires.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                    <span className="visually-hidden">Chargement...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger" className="rounded-3">
                <div className="d-flex align-items-center">
                    <i className="ri-error-warning-line fs-4 me-3 text-danger"></i>
                    <div>
                        <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                        <p className="mb-0 small">{error}</p>
                        <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchInventaires}>
                            <i className="ri-refresh-line me-1"></i>Réessayer
                        </Button>
                    </div>
                </div>
            </Alert>
        );
    }

    return (
        <>
            {/* Filtres modernes */}
            <Card className="border-0 shadow-sm rounded-3 mb-4">
                <Card.Body className="py-3 px-4">
                    <Row className="align-items-center g-3">
                        <Col md={4}>
                            <div className="position-relative">
                                <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                                <Form.Control
                                    type="text"
                                    placeholder="Rechercher un inventaire..."
                                    value={filterText}
                                    onChange={(e) => setFilterText(e.target.value)}
                                    className="ps-5 rounded-pill"
                                />
                            </div>
                        </Col>
                        <Col md={3}>
                            <Form.Control
                                type="date"
                                value={dateDebut}
                                onChange={(e) => setDateDebut(e.target.value)}
                                className="rounded-pill"
                                placeholder="Date début"
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Control
                                type="date"
                                value={dateFin}
                                onChange={(e) => setDateFin(e.target.value)}
                                className="rounded-pill"
                                placeholder="Date fin"
                            />
                        </Col>
                        <Col md={2}>
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => {
                                    setFilterText('');
                                    setDateDebut('');
                                    setDateFin('');
                                }} 
                                className="w-100 rounded-pill"
                            >
                                <i className="ri-refresh-line me-2"></i>Réinitialiser
                            </Button>
                        </Col>
                    </Row>
                    <Row className="mt-3">
                        <Col className="text-end">
                            <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                <i className="ri-stack-line me-1"></i>
                                {filteredInventaires.length} inventaire(s)
                            </span>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Tableau avec pagination */}
            <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0 custom-table">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4">Référence</th>
                                    <th>Date</th>
                                    <th>Validé le</th>
                                    <th>Par</th>
                                    <th className="text-center">Produits</th>
                                    <th className="text-center">Écart total</th>
                                    <th>Statut</th>
                                    <th className="text-center pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.length > 0 ? (
                                    currentItems.map((inv, index) => (
                                        <tr key={inv.id_inventaire} style={{
                                            animation: `fadeIn 0.3s ease ${index * 0.05}s both`
                                        }}>
                                            <td className="ps-4">
                                                <Link to={`/stock/inventaire/${inv.id_inventaire}`} className="table-link">
                                                    {inv.reference_inventaire}
                                                </Link>
                                            </td>
                                            <td>{formatDate(inv.date_inventaire)}</td>
                                            <td>{inv.date_validation ? formatDate(inv.date_validation) : '-'}</td>
                                            <td>{inv.prenom_utilisateur} {inv.nom_utilisateur}</td>
                                            <td className="text-center">
                                                <Badge className="rounded-pill px-3" style={{ background: '#e2f3f5', color: '#fff' }}>
                                                    {inv.nb_produits || 0}
                                                </Badge>
                                            </td>
                                            <td className="text-center">
                                                <span className={`fw-semibold ${inv.total_ecart > 0 ? 'text-success' : inv.total_ecart < 0 ? 'text-danger' : ''}`}>
                                                    {inv.total_ecart > 0 ? '+' : ''}{inv.total_ecart || 0}
                                                </span>
                                            </td>
                                            <td>{getStatutBadge(inv.statut)}</td>
                                            <td className="text-center pe-4">
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                                                    <Button 
                                                        variant="outline-info" 
                                                        size="sm"
                                                        className="action-btn"
                                                        onClick={() => navigate(`/stock/inventaire/${inv.id_inventaire}`)}
                                                    >
                                                        <i className="ri-eye-line"></i>
                                                    </Button>
                                                </OverlayTrigger>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="text-center py-5">
                                            <div className="empty-state">
                                                <i className="ri-history-line empty-icon text-primary" style={{ fontSize: '4rem' }}></i>
                                                <h5 className="mt-3">Aucun inventaire trouvé</h5>
                                                <p className="text-muted mb-3">Aucun inventaire enregistré dans l'historique</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center p-3 border-top">
                            <div className="text-muted small">
                                <i className="ri-file-list-line me-1"></i>
                                Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredInventaires.length)} sur {filteredInventaires.length} inventaires
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                {renderPagination()}
                                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                            </Pagination>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                /* Pagination */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
            `}</style>
        </>
    );
}